import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { api, Me, Role, roleAtLeast } from "./api";

interface AuthState {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  can: (min: Role) => boolean;
}

const AuthCtx = createContext<AuthState>({
  me: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
  can: () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      setMe(await api.me());
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      setMe(null);
      window.location.href = "/login";
    }
  };

  const can = (min: Role) => (me ? roleAtLeast(me.role, min) : false);

  return <AuthCtx.Provider value={{ me, loading, refresh, logout, can }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
